/**
 * ARStatusWorkflow - manage allowed status transitions for Architectural Requests.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Paper, Grid, TextField, MenuItem,
  Chip, IconButton, Tooltip, Alert, Snackbar, CircularProgress,
} from "@mui/material";
import AddIcon    from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import api from "../../utils/api";

const STATUSES = ['Draft','Submitted','Under Review','Pending Info','Approved','Denied','Completed','Closed','Cancelled'];
const cardSx = { borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", boxShadow:"0 4px 16px rgba(0,0,0,0.05)", p:2.5, mb:2.5 };
const primaryBtnSx = { backgroundColor:"#2f63ad", color:"white", fontWeight:700, borderRadius:"10px", textTransform:"none", boxShadow:"none", "&:hover":{ backgroundColor:"#1f4f94", boxShadow:"none" } };
const fieldSx = { "& .MuiOutlinedInput-root":{ borderRadius:"10px" } };

const ARStatusWorkflow = () => {
  const [transitions, setTransitions] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [from,        setFrom]        = useState("");
  const [to,          setTo]          = useState("");
  const [snack,       setSnack]       = useState({ open:false, msg:"", sev:"success" });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/ar-status-workflow"); setTransitions(r.data || []); }
    catch { setSnack({ open:true, msg:"Failed to load workflow.", sev:"error" }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!from || !to) { setSnack({ open:true, msg:"Select both from and to status.", sev:"error" }); return; }
    if (from === to)  { setSnack({ open:true, msg:"From and To cannot be the same.", sev:"error" }); return; }
    try {
      await api.post("/ar-status-workflow", { from_status: from, to_status: to });
      setFrom(""); setTo("");
      setSnack({ open:true, msg:"Transition added.", sev:"success" });
      load();
    } catch { setSnack({ open:true, msg:"Failed to add transition.", sev:"error" }); }
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/ar-status-workflow/${id}`); load(); setSnack({ open:true, msg:"Removed.", sev:"success" }); }
    catch { setSnack({ open:true, msg:"Failed to delete.", sev:"error" }); }
  };

  // Group by from_status
  const grouped = {};
  for (const t of transitions) {
    if (!grouped[t.from_status]) grouped[t.from_status] = [];
    grouped[t.from_status].push(t);
  }

  return (
    <Box>
      <Box sx={{ mb:3 }}>
        <Typography variant="h6" sx={{ fontWeight:700, color:"#223142" }}>Status Workflow</Typography>
        <Typography variant="body2" sx={{ color:"#6b778c" }}>
          Define which status transitions are allowed. Only configured transitions will be available when changing a request's status.
          If no transitions are configured for a status, all changes are permitted (backwards compatible).
        </Typography>
      </Box>

      {/* Add new transition */}
      <Paper elevation={0} sx={cardSx}>
        <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Add Transition</Typography>
        <Box sx={{ display:"flex", gap:2, alignItems:"center", flexWrap:"wrap" }}>
          <TextField select size="small" label="From Status" value={from} onChange={e=>setFrom(e.target.value)} sx={{ ...fieldSx, minWidth:160 }}>
            {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <ArrowForwardIcon sx={{ color:"#6b778c" }} />
          <TextField select size="small" label="To Status" value={to} onChange={e=>setTo(e.target.value)} sx={{ ...fieldSx, minWidth:160 }}>
            {STATUSES.filter(s => s !== from).map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} sx={primaryBtnSx}>Add</Button>
        </Box>
      </Paper>

      {/* Current transitions */}
      <Paper elevation={0} sx={cardSx}>
        <Typography variant="subtitle2" sx={{ fontWeight:700, color:"#223142", mb:2 }}>Current Transitions</Typography>
        {loading ? <CircularProgress size={24} /> : Object.keys(grouped).length === 0 ? (
          <Alert severity="info" sx={{ borderRadius:2 }}>
            No transitions configured. All status changes are currently allowed.
          </Alert>
        ) : (
          <Box sx={{ display:"flex", flexDirection:"column", gap:1.5 }}>
            {Object.entries(grouped).map(([fromStatus, tos]) => (
              <Box key={fromStatus} sx={{ display:"flex", alignItems:"center", gap:1.5, flexWrap:"wrap",
                p:1.5, borderRadius:"10px", bgcolor:"rgba(47,99,173,0.03)", border:"1px solid rgba(47,99,173,0.08)" }}>
                <Chip label={fromStatus} sx={{ fontWeight:700, bgcolor:"rgba(47,99,173,0.1)", color:"#2f63ad", borderRadius:"8px" }} />
                <ArrowForwardIcon sx={{ color:"#9ca3af", fontSize:18 }} />
                <Box sx={{ display:"flex", gap:0.75, flexWrap:"wrap", flex:1 }}>
                  {tos.map(t => (
                    <Chip key={t.id} label={t.to_status}
                      onDelete={() => handleDelete(t.id)}
                      deleteIcon={<DeleteIcon fontSize="small" />}
                      sx={{ fontWeight:600, bgcolor:"rgba(34,197,94,0.1)", color:"#16a34a", borderRadius:"8px" }} />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={3500} onClose={()=>setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:"top", horizontal:"center" }}>
        <Alert severity={snack.sev} onClose={()=>setSnack(s=>({...s,open:false}))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ARStatusWorkflow;
